// Dedicated IP Management — warmup monitoring and IP lifecycle actions

import { Network } from 'lucide-react'
import { DataTable } from '@/components/ui/data-table'
import { LoadingPage } from '@/components/ui/loading'
import { MetricCard } from '@/components/ui/metric-card'
import { StatusBadge } from '@/components/ui/status-badge'
import type { IpListRow } from '@/hooks/use-ips'
import { useAdvanceIp, useIps, usePauseIp, useResetIp, useResumeIp } from '@/hooks/use-ips'

const HEALTH_STYLES = {
  good: 'bg-green-100 text-green-700',
  fair: 'bg-yellow-100 text-yellow-700',
  poor: 'bg-red-100 text-red-700',
} as const

function healthLabel(row: IpListRow): keyof typeof HEALTH_STYLES {
  const bounceRate = row.today_bounce_rate ? Number(row.today_bounce_rate) : 0
  if (bounceRate > 0.1) return 'poor'
  if (bounceRate > 0.05) return 'fair'
  return 'good'
}

function ActionButtons({
  row,
  pauseIp,
  resumeIp,
  resetIp,
  advanceIp,
}: {
  row: IpListRow
  pauseIp: (args: { id: string; reason: string }) => void
  resumeIp: (id: string) => void
  resetIp: (id: string) => void
  advanceIp: (id: string) => void
}): React.JSX.Element {
  const isPaused = row.status === 'paused'
  const isWarming = row.status === 'warming'

  return (
    <div className="flex items-center gap-1.5">
      {isPaused ? (
        <button
          type="button"
          onClick={() => resumeIp(row.id)}
          className="rounded-md bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100"
        >
          Resume
        </button>
      ) : (
        <button
          type="button"
          onClick={() => pauseIp({ id: row.id, reason: 'Manual pause from admin' })}
          className="rounded-md bg-yellow-50 px-2.5 py-1 text-xs font-medium text-yellow-700 hover:bg-yellow-100"
        >
          Pause
        </button>
      )}
      <button
        type="button"
        onClick={() => resetIp(row.id)}
        className="rounded-md bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
      >
        Reset
      </button>
      {isWarming && (
        <button
          type="button"
          onClick={() => advanceIp(row.id)}
          className="rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
        >
          Advance
        </button>
      )}
    </div>
  )
}

export function IpsPage(): React.JSX.Element {
  const ipsQuery = useIps()
  const pauseMutation = usePauseIp()
  const resumeMutation = useResumeIp()
  const resetMutation = useResetIp()
  const advanceMutation = useAdvanceIp()

  const ips = ipsQuery.data?.ips ?? []

  const totalCount = ips.length
  const activeCount = ips.filter(
    (ip) => ip.status === 'warming' || ip.status === 'completed',
  ).length
  const warmingCount = ips.filter((ip) => ip.status === 'warming').length
  const pausedCount = ips.filter((ip) => ip.status === 'paused').length

  const columns = [
    {
      key: 'ip_address',
      label: 'IP Address',
      sortable: true,
      render: (row: IpListRow) => <span className="font-mono text-sm">{row.ip_address}</span>,
    },
    {
      key: 'account_id',
      label: 'Account',
      render: (row: IpListRow) => (
        <span className="font-mono text-xs text-text-secondary">{row.account_id}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row: IpListRow) => <StatusBadge status={row.status} />,
    },
    {
      key: 'warming_day',
      label: 'Warmup Stage',
      sortable: true,
      render: (row: IpListRow) => (
        <span className="text-sm">
          Day {row.warming_day} / {row.daily_limit.toLocaleString()} limit
        </span>
      ),
    },
    {
      key: 'health',
      label: 'Health Score',
      render: (row: IpListRow) => {
        const h = healthLabel(row)
        return (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${HEALTH_STYLES[h]}`}
          >
            {h}
          </span>
        )
      },
    },
    {
      key: 'volume',
      label: 'Volume/day',
      sortable: true,
      render: (row: IpListRow) => (
        <span className="text-sm">
          {(row.today_sent ?? 0).toLocaleString()} / {row.daily_limit.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row: IpListRow) => (
        <ActionButtons
          row={row}
          pauseIp={(args) => pauseMutation.mutate(args)}
          resumeIp={(id) => resumeMutation.mutate(id)}
          resetIp={(id) => resetMutation.mutate(id)}
          advanceIp={(id) => advanceMutation.mutate(id)}
        />
      ),
    },
  ]

  if (ipsQuery.isLoading) {
    return <LoadingPage message="Loading dedicated IPs..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Network className="h-6 w-6 text-admin-500" />
        <h1 className="text-2xl font-semibold text-text-primary">Dedicated IPs</h1>
      </div>

      {/* Metric cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard label="Total IPs" value={String(totalCount)} />
        <MetricCard
          label="Active"
          value={String(activeCount)}
          variant="success"
          sub="warming + completed"
        />
        <MetricCard label="Warming" value={String(warmingCount)} variant="warning" />
        <MetricCard
          label="Paused"
          value={String(pausedCount)}
          variant={pausedCount > 0 ? 'danger' : 'default'}
        />
      </div>

      {/* Data table */}
      <DataTable columns={columns} data={ips} emptyMessage="No dedicated IPs configured" />
    </div>
  )
}
